import { useEffect, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'
import axios from 'axios'
import { setUserInfo, /* logout, */ resetUserState } from '../slices/userSlice'
import { buildRedirectParam } from '../utils/redirect'

const GuestGuard = () => {
  const dispatch = useDispatch()
  const location = useLocation()
  const userInfo = useSelector((state) => state.user?.userInfo || state.userLogin?.userInfo || null)

  const fetchInFlight = useRef(false)
  const lastFetchedPath = useRef('')

  const isGuestAllowedPath = (pathname) => {
    const rawPath = pathname || '/'
    const path = rawPath.replace(/\/+$/, '') || '/'
    const allowedExact = ['/', '/login', '/register', '/resendconfirmation', '/forgotpw', '/contact', '/contactSupport', '/shipping', '/shippingrate', '/payment', '/placeorder', '/cart']
    const allowedPrefixes = ['/order', '/product', '/search', '/page']
    if (path === '/cart' || path.startsWith('/cart/')) return true
    // block admin for guests
    if (path.startsWith('/admin')) return false
    return (
      allowedExact.includes(path) ||
      allowedPrefixes.some((p) => path === p || path.startsWith(p + '/'))
    )
  }

  // Only attempt session restore on checkout and order routes (not product/search/login)
  const shouldAttemptSessionRestore = (pathname) => {
    const path = (pathname || '/').replace(/\/+$/, '') || '/'
    const restoreExact = ['/shipping', '/shippingrate', '/placeorder']
    const restorePrefixes = ['/order']
    return restoreExact.includes(path) || restorePrefixes.some((p) => path === p || path.startsWith(p + '/'))
  }

  const guestCookiePresent = () => typeof document !== 'undefined' && document.cookie.includes('guestToken=')

  useEffect(() => {
    const maybeAutoLoginGuest = async () => {
      if (!shouldAttemptSessionRestore(location.pathname)) return
      if (userInfo && (userInfo.isGuest || userInfo._id)) return
      if (!guestCookiePresent()) return
      if (fetchInFlight.current) return
      if (lastFetchedPath.current === location.pathname) return

      fetchInFlight.current = true
      try {
        const { data } = await axios.get('/api/users/session', { withCredentials: true })
        if (data) {
          dispatch(setUserInfo(data))
          try { localStorage.setItem('userInfo', JSON.stringify(data)) } catch {}
        }
      } catch (_) {
        // ignore
      } finally {
        lastFetchedPath.current = location.pathname
        fetchInFlight.current = false
      }
    }

    maybeAutoLoginGuest()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally excludes 'userInfo': re-running on every userInfo change (e.g. right after this effect's own dispatch(setUserInfo)) would fight the fetchInFlight/lastFetchedPath guards above
  }, [location.pathname, dispatch])

  useEffect(() => {
    if (userInfo?.isGuest) {
      if (!isGuestAllowedPath(location.pathname)) {
        try { localStorage.removeItem('userInfo') } catch {}
        dispatch(resetUserState())
        const raw = location.pathname + (location.search || '')
        const redirect = buildRedirectParam(raw)
        window.location.href = `/login?redirect=${redirect}`
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally excludes 'location.search': only the redirect target uses it, re-running on query-string-only changes isn't needed here
  }, [location.pathname, userInfo?.isGuest, dispatch])

  return null
}

export default GuestGuard
